// namat#30: how a manager places a beneficiary in a group when the
// beneficiary may already be in another group of the same organization.
// addGroupMember refuses a second group (SubgroupConflictError); the manager
// is then offered to MOVE them instead, and on refusal or failure gets the
// rule spelled out. Shared by every manager add path so they behave alike.
import {
  addGroupMember,
  getGroupName,
  moveGroupMember,
  SectionNotJoinableError,
  SubgroupConflictError,
} from '@/lib/firebase/queries/group-members';
import {
  membershipConflictMessage,
  membershipMoveConfirm,
  sectionNotJoinableMessage,
} from './membershipConflictCopy';

export type AddMemberOutcome =
  | { kind: 'added' }
  | { kind: 'moved'; fromGroupId: string }
  /** The manager chose not to move them: nothing changed. */
  | { kind: 'declined' }
  /** Refused by a rule; `message` says which. */
  | { kind: 'blocked'; message: string }
  /** addGroupMember reported a failure (it already toasted the Firebase error). */
  | { kind: 'failed' };

export async function addMemberOrOfferMove(input: {
  groupId: string;
  groupName: string;
  userId: string;
  byUserId: string;
  rtl: boolean;
  /** Asks the manager to confirm the move; window.confirm by default. */
  confirm?: (message: string) => boolean;
}): Promise<AddMemberOutcome> {
  const { groupId, groupName, userId, byUserId, rtl } = input;
  const confirm = input.confirm ?? ((message: string) => window.confirm(message));
  try {
    return (await addGroupMember(groupId, userId, byUserId)) ? { kind: 'added' } : { kind: 'failed' };
  } catch (error) {
    if (error instanceof SectionNotJoinableError) {
      return { kind: 'blocked', message: sectionNotJoinableMessage(rtl) };
    }
    if (!(error instanceof SubgroupConflictError)) throw error;
    const fromName = await getGroupName(error.existingGroupId);
    if (!confirm(membershipMoveConfirm(rtl, fromName, groupName))) return { kind: 'declined' };
    try {
      if (await moveGroupMember(error.existingGroupId, groupId, userId, byUserId)) {
        return { kind: 'moved', fromGroupId: error.existingGroupId };
      }
    } catch (moveError) {
      console.error('Moving the member failed:', moveError);
    }
    return { kind: 'blocked', message: membershipConflictMessage('manager', rtl, fromName) };
  }
}
